import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { UploadCloud, File, X } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { useTranslation } from "react-i18next";

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  accept?: string;
  maxSize?: number;
  value?: File | null;
  className?: string;
}

export function FileUpload({
  onFileSelect,
  accept = ".csv",
  maxSize = 50 * 1024 * 1024, // 50MB default
  value,
  className
}: FileUploadProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  // Converter a string de accept para o formato esperado pelo react-dropzone
  const getAcceptFormat = () => {
    if (!accept || accept === "*") return undefined;
    
    // Se for um MIME type completo, usá-lo diretamente
    if (accept.includes('/')) {
      return { [accept]: [] };
    }
 
    // Se for uma extensão, mapear para MIME types comuns
    const extensionMap: Record<string, string> = {
      '.csv': 'text/csv',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.pdf': 'application/pdf',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.txt': 'text/plain',
      '.json': 'application/json',
      '.xml': 'application/xml',
      '.md': 'text/markdown',
      '.markdown': 'text/markdown',
      '.mdown': 'text/markdown',
      '.mkd': 'text/markdown',
      '.mkdn': 'text/markdown',
      '.mdtxt': 'text/markdown',
      '.mdtext': 'text/markdown'

    };
    
    // Remover o ponto inicial se presente
    const ext = accept.startsWith('.') ? accept : `.${accept}`;
    const mimeType = extensionMap[ext] || 'application/octet-stream';
    
    return { [mimeType]: [ext] };
  };

  const acceptFormat = getAcceptFormat();

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setError(null);

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0].code === "file-too-large") {
        setError(t('knowledge.imports.upload.fileTooLarge', { size: formatBytes(maxSize) }));
      } else if (rejection.errors[0].code === "file-invalid-type") {
        setError(t('knowledge.imports.upload.unsupportedType', { type: accept }));
      } else {
        setError(t('knowledge.imports.upload.error'));
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }, [maxSize, accept, onFileSelect, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptFormat,
    maxSize,
    multiple: false
  });

  const removeFile = () => {
    onFileSelect(null);
    setError(null);
  };

  function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  return (
    <div className={cn("w-full my-1", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-6 transition-colors",
          isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25",
          error ? "border-destructive/50 bg-destructive/5" : "",
          "hover:border-primary/50 hover:bg-primary/5",
          value ? "bg-primary/5" : ""
        )}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          {value ? (
            <>
              <div className="flex items-center gap-2 text-sm">
                <File className="h-4 w-4" />
                <span className="font-medium">{value.name}</span>
                <span className="text-muted-foreground">({formatBytes(value.size)})</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <UploadCloud className="h-10 w-10 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {t('knowledge.imports.upload.dragText')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {accept === "*"
                    ? t('knowledge.imports.upload.anyFile')
                    : t('knowledge.imports.upload.acceptedType', { type: accept })}
                  {maxSize && ` ${t('knowledge.imports.upload.maxSizeText', { size: formatBytes(maxSize) })}`}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      
      {error && (
        <p className="mt-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
